import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { History, Clock, User, FileText, Filter, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuditLogsPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', pagination.limit);
      params.append('skip', pagination.skip);
      
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.skip]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/summary?days=7`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs, fetchSummary]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({ entity_type: '', action: '', start_date: '', end_date: '' });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      // dd/MM/yyyy HH:mm in NZ time
      return new Intl.DateTimeFormat('en-NZ', {
        timeZone: 'Pacific/Auckland',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    } catch {
      return isoString;
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      clock_in: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      admin_edit: 'bg-yellow-100 text-yellow-800',
      clock_out: 'bg-purple-100 text-purple-800',
      delete: 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-slate-100 text-slate-800';
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      time_entry: <Clock className="w-4 h-4" />,
      break: <Clock className="w-4 h-4" />,
      project: <FileText className="w-4 h-4" />,
      employee: <User className="w-4 h-4" />
    };
    return icons[entityType] || <FileText className="w-4 h-4" />;
  };

  const formatChanges = (log) => {
    if (!log.changes && !log.old_values && !log.new_values) return null;
    
    if (log.changes && Object.keys(log.changes).length > 0) {
      return Object.entries(log.changes).map(([key, value]) => (
        <div key={key} className="text-xs text-slate-500">
          <span className="font-medium">{key}:</span>{' '}
          <span className="text-red-500 line-through">{value.from || 'null'}</span>
          {' → '}
          <span className="text-green-600">{value.to || 'null'}</span>
        </div>
      ));
    }
    
    if (log.action === 'clock_in' && log.new_values) {
      return (
        <div className="text-xs text-slate-500">
          Clocked in at {log.new_values.clock_in ? new Date(log.new_values.clock_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
          {log.new_values.project_id && ` • Project: ${log.new_values.project_id.substring(0, 8)}...`}
        </div>
      );
    }
    
    if (log.action === 'clock_out' && log.new_values) {
      return (
        <div className="text-xs text-slate-500">
          Total hours: {log.new_values.total_hours || 0}h
        </div>
      );
    }
    
    return null;
  };

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin' || user?.is_manager;

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Access Restricted</h3>
            <p className="text-slate-500 text-sm">Audit logs are only available to administrators</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <History className="w-6 h-6" />
          Audit Logs
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Track all changes made to timesheets and employee data
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Events (7 days)</p>
              <p className="text-2xl font-bold text-slate-900">{summary.total_events}</p>
            </CardContent>
          </Card>
          {summary.by_action?.slice(0, 3).map((item) => (
            <Card key={item.action}>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 capitalize">{item.action.replace('_', ' ')}</p>
                <p className="text-2xl font-bold text-slate-900">{item.count}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Select value={filters.entity_type} onValueChange={(v) => handleFilterChange('entity_type', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="time_entry">Time Entry</SelectItem>
                <SelectItem value="break">Break</SelectItem>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.action} onValueChange={(v) => handleFilterChange('action', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="clock_in">Clock In</SelectItem>
                <SelectItem value="clock_out">Clock Out</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="admin_edit">Admin Edit</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              placeholder="Start Date"
            />
            
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              placeholder="End Date"
            />
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearFilters} className="flex-1">
                Clear
              </Button>
              <Button onClick={fetchLogs} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity Log</CardTitle>
          <CardDescription>
            Showing {logs.length} of {pagination.total} events
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No audit logs found
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log, index) => (
                <div 
                  key={`${log.timestamp}-${index}`}
                  className="flex items-start gap-4 p-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-slate-100">
                    {getEntityIcon(log.entity_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={getActionColor(log.action)}>
                        {log.action.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm font-medium text-slate-900">
                        {log.user_name}
                      </span>
                      <span className="text-xs text-slate-400">
                        modified {log.entity_type.replace('_', ' ')}
                      </span>
                    </div>
                    {log.metadata?.target_user_name && log.metadata.target_user_name !== log.user_name && (
                      <p className="text-sm text-slate-500 mt-1">
                        → Affected: {log.metadata.target_user_name}
                      </p>
                    )}
                    {formatChanges(log)}
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDate(log.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <Button
                variant="outline"
                disabled={pagination.skip === 0}
                onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
              </span>
              <Button
                variant="outline"
                disabled={pagination.skip + pagination.limit >= pagination.total}
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
